import { DEFAULT_LOCALE, isSupportedLocale } from './locale';

export const PREFERRED_LOCALE_STORAGE_KEY = 'trivox.preferredLocale';
export const PREFERRED_LOCALE_COOKIE = 'trivox_locale';
const GEO_COUNTRY_SESSION_KEY = 'trivox.geoCountry';

/**
 * ISO country → site locale only when that language is clearly the country's
 * primary language and we actually ship it. Multilingual countries stay off
 * this map (Belgium, Luxembourg, Canada, …) unless the browser language is
 * already one we support. French is not a site locale, so FR/MC/etc. stay English.
 */
export const COUNTRY_PRIMARY_LOCALE = Object.freeze({
  DE: 'de',
  AT: 'de',
  CH: 'de',
  LI: 'de',
  NL: 'nl',
  IT: 'it',
  SM: 'it',
  VA: 'it',
  FI: 'fi',
  RU: 'ru',
  BY: 'ru',
  CN: 'zh',
});

// Include Lighthouse/PageSpeed so client locale redirects do not rewrite
 // canonical/hreflang while the audited main document URL stays on `/`.
const CRAWLER_UA = /bot|crawl|spider|slurp|facebookexternalhit|preview|whatsapp|telegram|discordbot|linkedinbot|embedly|quora|pinterest|redditbot|applebot|bingpreview|yandex|baidu|semrush|ahrefs|dotbot|mj12bot|petalbot|lighthouse|chrome-lighthouse|pagespeed|gtmetrix|pingdom|screaming frog/i;

export function isCrawlerUserAgent(userAgent = '') {
  return CRAWLER_UA.test(String(userAgent || ''));
}

export function normalizeLanguageTag(tag = '') {
  const raw = String(tag || '').trim().replace(/_/g, '-');
  if (!raw) return null;
  try {
    const locale = new Intl.Locale(raw);
    const language = String(locale.language || '').toLowerCase();
    const region = locale.region ? String(locale.region).toUpperCase() : null;
    return { language, region, tag: raw };
  } catch {
    const [language, region] = raw.split('-');
    if (!language) return null;
    return {
      language: language.toLowerCase(),
      region: region ? region.toUpperCase() : null,
      tag: raw,
    };
  }
}

export function localeFromLanguageTag(tag) {
  const parsed = normalizeLanguageTag(tag);
  if (!parsed) return null;
  if (parsed.language === 'zh') return 'zh';
  if (isSupportedLocale(parsed.language)) return parsed.language;
  return null;
}

export function localeFromCountryCode(countryCode) {
  const code = String(countryCode || '').trim().toUpperCase();
  if (!code) return null;
  const locale = COUNTRY_PRIMARY_LOCALE[code];
  return locale && isSupportedLocale(locale) ? locale : null;
}

function collectLanguageTags(languages) {
  if (Array.isArray(languages) && languages.length) {
    return languages.map((entry) => String(entry || '').trim()).filter(Boolean);
  }
  return [];
}

export function resolvePreferredLocale({ saved, languages = [], country = null } = {}) {
  if (saved && isSupportedLocale(saved)) return saved;

  const tags = collectLanguageTags(languages);
  for (const tag of tags) {
    const locale = localeFromLanguageTag(tag);
    if (locale && locale !== DEFAULT_LOCALE) return locale;
  }

  const fromCountry = localeFromCountryCode(country);
  if (fromCountry) return fromCountry;

  for (const tag of tags) {
    const parsed = normalizeLanguageTag(tag);
    const fromRegion = localeFromCountryCode(parsed?.region);
    if (fromRegion) return fromRegion;
  }

  return DEFAULT_LOCALE;
}

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  const match = String(document.cookie || '')
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  if (!match) return null;
  try {
    return decodeURIComponent(match.slice(prefix.length));
  } catch {
    return match.slice(prefix.length);
  }
}

function writeCookie(name, value) {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

export function readSavedLocale() {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(PREFERRED_LOCALE_STORAGE_KEY);
    if (isSupportedLocale(stored)) return stored;
  } catch {
    // ignore storage access errors
  }
  const cookie = readCookie(PREFERRED_LOCALE_COOKIE);
  return isSupportedLocale(cookie) ? cookie : null;
}

export function persistPreferredLocale(locale) {
  if (!isSupportedLocale(locale) || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PREFERRED_LOCALE_STORAGE_KEY, locale);
  } catch {
    // ignore storage access errors
  }
  writeCookie(PREFERRED_LOCALE_COOKIE, locale);
}

export function browserLanguageTags() {
  if (typeof navigator === 'undefined') return [];
  const tags = [];
  if (Array.isArray(navigator.languages)) tags.push(...navigator.languages);
  if (navigator.language) tags.push(navigator.language);
  return tags.filter(Boolean);
}

function parseCloudflareTrace(text) {
  const match = String(text || '').match(/(?:^|\n)loc=([A-Z]{2})(?:\n|$)/);
  return match ? match[1] : null;
}

export async function detectCountryCode({ timeoutMs = 800 } = {}) {
  if (typeof window === 'undefined') return null;
  try {
    const cached = window.sessionStorage.getItem(GEO_COUNTRY_SESSION_KEY);
    if (cached === '') return null;
    if (cached && /^[A-Z]{2}$/.test(cached)) return cached;
  } catch {
    // ignore
  }

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = setTimeout(() => controller?.abort(), timeoutMs);
  try {
    const response = await fetch('/cdn-cgi/trace', {
      method: 'GET',
      credentials: 'omit',
      signal: controller?.signal,
    });
    if (!response.ok) throw new Error('trace unavailable');
    const country = parseCloudflareTrace(await response.text());
    try {
      window.sessionStorage.setItem(GEO_COUNTRY_SESSION_KEY, country || '');
    } catch {
      // ignore
    }
    return country;
  } catch {
    try {
      window.sessionStorage.setItem(GEO_COUNTRY_SESSION_KEY, '');
    } catch {
      // ignore
    }
    return null;
  } finally {
    clearTimeout(timer);
  }
}
